import { TournamentFormatEnum } from '../../enums/enums';
import {
  ApiResponse,
  DeclareTournamentMatchWinnerPayload,
  Team,
  TournamentCreateRequest,
  TournamentDetailsData,
  TournamentMatchScoreValue,
  UpdateScorePayloadType,
  TournamentMatchUpdatePayload,
  UpdateTournamentPayload,
} from '../../types/types';
import { client } from '../client';
import { urls } from '../endpoints';

export const createTournamentRequest = async (
  tournamentData: TournamentCreateRequest,
) => {
  try {
    const response = await client.post<ApiResponse<number>>(
      urls.createTournament,
      tournamentData,
    );
    return response;
  } catch (error) {
    throw error;
  }
};

const getPairedUnpairedParticipantsRequest = async (tournamentId: number) => {
  try {
    const response = await client.get<ApiResponse<TournamentDetailsData>>(
      urls.getPairedUnpairedParticipants(tournamentId),
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching paired and unpaired participants:', error);
    throw error;
  }
};

const generateBracketRequest = async (
  tournamentId: number,
  tournamentFormat: TournamentFormatEnum,
) => {
  try {
    const response = await client.post<ApiResponse<null>>(
      urls.generateBracket(tournamentId),
      { tournamentId, tournamentFormat },
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error('Error generating bracket:', error);
    throw error;
  }
};

const getTournamentBracketRequest = async (tournamentId: number) => {
  try {
    const response = await client.get<ApiResponse<any>>(
      urls.getTournamentBracket(tournamentId),
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching tournament bracket:', error);
    throw error;
  }
};

const updateTournamentRequest = async (
  tournamentData: UpdateTournamentPayload,
) => {
  try {
    const response = await client.put<ApiResponse<number>>(
      urls.updateTournament(tournamentData.tournamentId),
      tournamentData,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const deleteTournamentRequest = async (tournamentId: number) => {
  try {
    const response = await client.delete<ApiResponse<number>>(
      urls.deleteTournament(tournamentId),
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getTournamentTeamsRequest = async (
  tournamentId: number,
): Promise<Team[]> => {
  try {
    const response = await client.get<ApiResponse<Team[]>>(
      urls.getTournamentTeams(tournamentId),
    );
    return response.data?.value || [];
  } catch (error) {
    console.error('Error fetching tournament teams:', error);
    throw error;
  }
};

const updateTournamentMatchDetailsRequest = async (
  tournamentMatchId: number,
  tournamentMatchData: TournamentMatchUpdatePayload,
) => {
  try {
    const response = await client.put<ApiResponse<number>>(
      urls.updateTournamentMatch(tournamentMatchId),
      tournamentMatchData,
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getTournamentMatchScoreRequest = async (tournamentMatchId: number) => {
  try {
    const response = await client.get<ApiResponse<TournamentMatchScoreValue>>(
      urls.getTournamentMatchScore(tournamentMatchId),
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching tournament match score :', error);
    throw error;
  }
};

const declareWinnerRequest = async (
  tournamentId: number,
  payload: DeclareTournamentMatchWinnerPayload,
) => {
  try {
    const response = await client.post<ApiResponse>(
      urls.declareWinner(tournamentId, payload.tournamentMatchId),
      payload,
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error('Error declaring winner:', error);
    throw error;
  }
};

const directUpdateTournamentparticipantScoreRequest = async (
  payload: UpdateScorePayloadType,
) => {
  try {
    const response = await client.post<ApiResponse>(
      urls.directUpdateTournamentParticipantScore(payload.tournamentMatchId),
      payload,
    );
    if (response.status === 200) {
      return response.data;
    }
  } catch (error) {
    console.error('Error direct update tournament participant score:', error);
    throw error;
  }
};

export {
  getPairedUnpairedParticipantsRequest,
  generateBracketRequest,
  getTournamentBracketRequest,
  updateTournamentRequest,
  deleteTournamentRequest,
  getTournamentTeamsRequest,
  updateTournamentMatchDetailsRequest,
  getTournamentMatchScoreRequest,
  declareWinnerRequest,
  directUpdateTournamentparticipantScoreRequest,
};
